/**
 * buildWorkspaceRightTabs — ordering logic for the WorkspacePage right-pane tab bar.
 *
 * Extracted as a pure function so tab order/composition can be unit tested
 * without rendering the full WorkspacePage (which pulls in services, auth
 * context, and a dozen child panes).
 */

export type WorkspaceRightTabId =
  | 'draft'
  | 'documents'
  | 'fto'
  | 'fto_claims'
  | 'patentability'
  | 'patent_draft'
  | 'industrialization';

export interface WorkspaceRightTab {
  id: WorkspaceRightTabId;
  label: string;
}

export interface BuildWorkspaceRightTabsParams {
  t: (key: string, fallback: string) => string;
  kbId?: string | null;
  kbName?: string | null;
  isFtoField: boolean;
  isPatentabilityField: boolean;
  isPatentApplicationField: boolean;
  isIndustrializationField: boolean;
}

export function buildWorkspaceRightTabs(params: BuildWorkspaceRightTabsParams): WorkspaceRightTab[] {
  const {
    t,
    kbId,
    kbName,
    isFtoField,
    isPatentabilityField,
    isPatentApplicationField,
    isIndustrializationField,
  } = params;

  const draftTab: WorkspaceRightTab = { id: 'draft', label: t('workspace.tabs.draft', 'Draft') };

  const otherTabs: WorkspaceRightTab[] = [
    ...(kbId ? [{ id: 'documents' as const, label: kbName || t('workspace.tabs.documents', 'Documents') }] : []),
    ...(isFtoField ? [{ id: 'fto' as const, label: t('workspace.tabs.ftoScope', 'FTO Scope') }] : []),
    ...(isFtoField ? [{ id: 'fto_claims' as const, label: t('workspace.tabs.ftoClaimsAnalysis', 'Claims Analysis') }] : []),
    ...(isPatentabilityField ? [{ id: 'patentability' as const, label: t('workspace.tabs.patentabilityAnalysis', 'Analysis') }] : []),
    ...(isPatentApplicationField ? [{ id: 'patent_draft' as const, label: t('workspace.tabs.patentDraft', 'Patent Draft') }] : []),
    ...(isIndustrializationField ? [{ id: 'industrialization' as const, label: t('workspace.tabs.industrializationDossier', 'Dossier') }] : []),
  ];

  // FTO Workspace: Draft is the right-most tab, not the left-most (issue #326).
  // All other field types keep Draft first, unchanged.
  return isFtoField ? [...otherTabs, draftTab] : [draftTab, ...otherTabs];
}
